import styled from 'styled-components';

import Button from 'components/shared/Button';
import { simpleDateAtTime } from 'lib/date';
import { PortalRetrievalStatus } from 'services/portalExtension';
import { Body, BodySmall, LabelBold } from 'styles/typography';

import { ScanCard as ScanCardModel } from './scanCards';

interface Props {
  card: ScanCardModel;
  onOpenResults: (card: ScanCardModel) => void;
  // Set only when this browser is the thing that sweeps this portal and the run is still waiting
  // to be driven. Absent for headless runs, for finished ones, and where there is no extension.
  onRunInBrowser?: () => void;
  // The live status of a sweep running right now, from the extension.
  runStatus?: PortalRetrievalStatus | 'starting';
}

// The extension's own status vocabulary, said in words a coordinator can act on.
const RUN_STATUS_COPY: Record<string, string> = {
  starting: 'Opening the portal…',
  running: 'Scanning the portal…',
  waiting: 'Waiting on the portal…',
  login: 'Signing in…',
};

/**
 * One scan. The three states the design asks for plus `running`, and the ONLY one that opens
 * anything is a success with rows behind it — a failed scan is terminal (nothing to look at) and
 * a scan that found nothing must not link to an empty table.
 */
export default function ScanCard({ card, onOpenResults, onRunInBrowser, runStatus }: Props) {
  const sweeping = runStatus !== undefined;

  return (
    <Card $failed={card.state === 'failed'} data-testid='scan-card' data-state={card.state}>
      <Header>
        <Title $failed={card.state === 'failed'} data-testid='scan-card-title'>
          {card.title}
        </Title>
        {/* The completion time, and only the completion time. A scan still running has none, and
            saying "scanned on <the time it was requested>" would be a different claim. */}
        {card.completedAt && <Timestamp data-testid='scan-card-time'>{simpleDateAtTime(card.completedAt)}</Timestamp>}
      </Header>
      {card.canOpenResults ? (
        <ResultsLink type='button' onClick={() => onOpenResults(card)} data-testid='scan-card-results'>
          {card.detail}
        </ResultsLink>
      ) : (
        <Detail data-testid='scan-card-detail'>
          {sweeping ? (RUN_STATUS_COPY[runStatus] ?? 'Scanning the portal…') : card.detail}
        </Detail>
      )}
      {/* In extension mode the run exists but nothing has swept yet — the sweep happens in this
          browser. Without this the card would sit at "running" indefinitely. */}
      {onRunInBrowser && (
        <Actions>
          <Button
            size='small'
            loading={sweeping}
            disabled={sweeping}
            onClick={onRunInBrowser}
            data-testid='scan-card-run'>
            {card.state === 'failed' ? 'Try this scan again' : 'Run this scan'}
          </Button>
        </Actions>
      )}
    </Card>
  );
}

const Card = styled.div<{ $failed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme, $failed }) => ($failed ? theme.colors.red100 : theme.colors.black10)};
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Title = styled(LabelBold)<{ $failed: boolean }>`
  font-size: 16px;
  color: ${({ theme, $failed }) => ($failed ? theme.colors.red100 : theme.colors.black100)};
`;

const Timestamp = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;

const Detail = styled(Body)`
  color: ${({ theme }) => theme.colors.black75};
`;

const ResultsLink = styled.button`
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-start;
`;
